<template>
  <div class="encounter-clinical-assessment">
    <v-form
      v-on:submit.prevent
      class="encounter-form"
    >
      <v-simple-table class="encounter-form__table">
        <template v-slot:default>
          <transition-group tag="tbody" name="slide">
          <tr key="LABEL_NEW_DIAGNOSIS">
            <td class="pb-4 title clear-box">
              <!-- Note: English by specs -->
              {{ 'New following diagnosis since last visit' }}
            </td>
          </tr>
          <template
            v-for="(questionConfig) in $store.getters['encounter/encounterConfigDisplayClinicalAssessment']"
          >
            <tr v-if="questionConfig.key === 'AGE'" :key="questionConfig.key" :class="'QUESTION_' + questionConfig.key">
              <td>
                {{ $ucfirst($t('age', { language : 'en' })) }}
                <encounter-info-icon :info="questionConfig.info" />
              </td>
              <td>
                <v-text-field
                    v-model="AGE"
                    :errorMessages="errorMessages['AGE']"
                    @blur="() => setErrorMessage('AGE')"
                    outlined
                    dense
                    :disabled="!canEditVisitData"
                    type="number"
                    min="0"
                    @keypress="onKeyPress"
                    @paste="onPaste"
                ></v-text-field>
              </td>
            </tr>

            <tr v-else-if="questionConfig.key === 'CHA2DS2_VASC'" :key="questionConfig.key" :class="'QUESTION_' + questionConfig.key">
              <td>
                {{ $ucfirst($t('questionnaires.CLINICAL_ASSESSMENT.questions.CHA2DS2_VASC.question', { catalog: 'questionnaires', language : 'en' })) }}
                <encounter-info-icon :info="questionConfig.info" />
              </td>
              <td>
                <v-text-field
                    :value="CHA2DS2_VASC"
                    outlined
                    dense
                    disabled
                ></v-text-field>
              </td>
            </tr>

            <tr v-else :key="questionConfig.key" :class="'QUESTION_' + questionConfig.key">
              <td>
                <span v-if="questionConfig.label">
                  {{ questionConfig.label }}
                </span>
                <span v-else>
                  {{$ucfirst($t(`questionnaires.CLINICAL_ASSESSMENT.questions.${questionConfig.key}.question`, { catalog: 'questionnaires', language : 'en' })) }}
                </span>
                  <encounter-info-icon :info="questionConfig.info" />
              </td>
              <td>
                <v-radio-group
                    v-model="self()[questionConfig.key]"
                    :errorMessages="errorMessages[questionConfig.key]"
                    @change="() => setErrorMessage(questionConfig.key)"
                    row
                    :disabled="!canEditVisitData"
                >
                  <v-radio
                      :label="$ucfirst($t('buttons.yes', {catalog: 'app_mobile', language: 'en'}))"
                      value="YES"
                  ></v-radio>
                  <v-radio
                      :label="$ucfirst($t('buttons.no', {catalog: 'app_mobile', language: 'en'}))"
                      value="NO"
                  ></v-radio>
                </v-radio-group>
              </td>
            </tr>
          </template>
          </transition-group>
        </template>
      </v-simple-table>
    </v-form>
  </div>
</template>

<script>
import questions from 'dashboard/store/encounter/questionnaires/clinical-assessment';
import encounterMixin from 'dashboard/mixins/encounter.mixin';
import EncounterInfoIcon from "../../components/Parts/EncounterInfoIcon";

/**
 * Component ClinicalAssessment.vue
 *
 * @module ClinicalAssessment
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/views/Encounter/ClinicalAssessment.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'ClinicalAssessment',
  components: {EncounterInfoIcon},
  mixins: [encounterMixin],
  props: {
    version: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      errorMessages: questions.reduce((accumulator, currentItem) => ({
        ...accumulator, [currentItem.key]: '',
      }), {}),
    };
  },
  computed: {
    ...questions.reduce((accumulator, currentValue) => ({
      ...accumulator, [currentValue.key]: {
        get() {
          return this.$store.getters[`encounter/${currentValue.key}`];
        },
        set(value) {
          this.$store.dispatch(`encounter/SET_${currentValue.key}`, value);
        },
      },
    }), {}),
  },
  methods: {
    updateMyDSSInfo: function(){
      this.$store.dispatch('encounter/getEncounterMyDSSInfo');
    },
    updateCHA2DS2VASC : function(){
      // If the CHA2DS2VASC score is not displayed no need to update.
      if (!this.$store.getters['encounter/getEncounterConfig']('clinicalAssessment.CHA2DS2_VASC', 'display'))
        return

      this.$store.dispatch('encounter/updateCHA2DS2VASCFromInput');
    },
    onKeyPress(event) {
      const onlyNumbers = /\d/
      if (!onlyNumbers.test(event.key)) {
        event.preventDefault();
      }
    },
    onPaste(event) {
      const pasted = event.clipboardData.getData('text/plain')
      const parsed = parseInt(pasted)
      event.preventDefault()

      if (!isNaN(parsed) && !(parsed < 0)) {
        this.AGE = parsed
      } else {
        this.AGE = ''
      }
    }
  },
  watch: {
    AGE() {
      this.updateCHA2DS2VASC()

      // meanBloodPressureAboveGoal field needs to be recalculated based on given age
      this.updateMyDSSInfo()
    },
    HISTORY_OF_HF() {
      this.updateCHA2DS2VASC()
    },
    NEW_DIAGNOSIS_HF() {
      this.updateCHA2DS2VASC()
    },
    THROMBOEMBOLISM() {
      this.updateCHA2DS2VASC()
    },
    TIA() {
      this.updateCHA2DS2VASC()
    },
    STROKE() {
      this.updateCHA2DS2VASC()
    },
    HYPERTENSION() {
      this.updateCHA2DS2VASC()
    },
    DIABETES_MELLITUS_TYPE1() {
      this.updateCHA2DS2VASC()
    },
    DIABETES_MELLITUS_TYPE2() {
      this.updateCHA2DS2VASC()
    },
  },
};
</script>
